import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import BASE_URL from "../../url";
import "../styles/JobCountReport.css";
import { usePersistentFilters } from "../../hooks/usePersistentFilters";
import {
  BarChart, Bar,
  AreaChart, Area,
  LineChart, Line,
  ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList
} from "recharts";
import {
  Calendar, Clock, BarChart2, Building2, Briefcase, Laptop, FileText, RotateCcw,
  Search, ChevronDown, X, TrendingUp, Activity, Layers, CheckCircle, AlertTriangle, Download
} from "lucide-react";
import MissingSubmissionsModal from "./MissingSubmissionsModal";
import { generateJobReportHtml, generateExecutiveDeckHtml } from "../utils/reportExportUtils";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const getWeekNumber = (d) => {
  const date = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
};

const formatDateKey = (dateKey, tp) => {
  if (!dateKey) return "";
  const d = new Date(dateKey + "T00:00:00Z");
  if (tp === "daily")   return d.toLocaleDateString("en-US", { timeZone: "UTC", month: "short", day: "numeric" });
  if (tp === "weekly")  return "W" + getWeekNumber(d) + " (" + d.toLocaleDateString("en-US", { timeZone: "UTC", month: "short", day: "numeric" }) + ")";
  if (tp === "monthly") return d.toLocaleDateString("en-US", { timeZone: "UTC", month: "short", year: "numeric" });
  if (tp === "yearly")  return String(d.getUTCFullYear());
  return dateKey;
};

// ─── Searchable Client Dropdown Component ─────────────────────────────────────
const SearchableClientSelect = ({ clients, selectedClient, onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredClients = (clients || []).filter(c =>
    (c.clientName || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="jcr-input-group" ref={containerRef} style={{ position: "relative" }}>
      <label>Client</label>
      <div
        onClick={() => setIsOpen(prev => !prev)}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "7px 12px", background: "white", border: "1px solid #cbd5e1",
          borderRadius: "6px", cursor: "pointer", fontSize: "13px", color: "#1e293b",
          minWidth: "180px", userSelect: "none", height: "36px", boxSizing: "border-box"
        }}
      >
        <span style={{ fontWeight: selectedClient ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {selectedClient ? selectedClient.name : "All Clients"}
        </span>
        <ChevronDown size={14} color="#64748b" style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s", marginLeft: "8px", flexShrink: 0 }} />
      </div>

      {isOpen && (
        <div
          style={{
            position: "absolute", top: "100%", left: 0, marginTop: "4px",
            width: "220px", background: "white", border: "1px solid #cbd5e1",
            borderRadius: "8px", boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
            zIndex: 99999, overflow: "hidden"
          }}
        >
          {/* Search Input Box */}
          <div style={{ padding: "8px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: "6px", background: "#f8fafc" }}>
            <Search size={14} color="#94a3b8" />
            <input
              type="text"
              autoFocus
              placeholder="Search client…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: "100%", border: "none", outline: "none", background: "transparent",
                fontSize: "12px", color: "#1e293b"
              }}
            />
            {search && (
              <X size={12} color="#94a3b8" style={{ cursor: "pointer" }} onClick={() => setSearch("")} />
            )}
          </div>

          {/* Options List */}
          <div style={{ maxHeight: "200px", overflowY: "auto" }}>
            <div
              onClick={() => { onSelect(null); setIsOpen(false); setSearch(""); }}
              style={{
                padding: "8px 12px", fontSize: "12px", cursor: "pointer",
                background: !selectedClient ? "#eff6ff" : "transparent",
                color: !selectedClient ? "#1e40af" : "#334155",
                fontWeight: !selectedClient ? 600 : 400
              }}
            >
              All Clients
            </div>
            {filteredClients.length === 0 ? (
              <div style={{ padding: "10px", fontSize: "12px", color: "#94a3b8", textAlign: "center" }}>No clients found</div>
            ) : (
              filteredClients.map(c => {
                const isSelected = selectedClient?.name === c.clientName;
                return (
                  <div
                    key={c.clientName}
                    onClick={() => { onSelect(c.clientName); setIsOpen(false); setSearch(""); }}
                    style={{
                      padding: "8px 12px", fontSize: "12px", cursor: "pointer",
                      background: isSelected ? "#e0f2fe" : "transparent",
                      color: isSelected ? "#0369a1" : "#334155",
                      fontWeight: isSelected ? 600 : 400
                    }}
                  >
                    {c.clientName}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Custom Tooltip: Jobs & Submissions (Top Chart) ──────────────────────────
const JobsAndSubsTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  const jobsEntry = payload.find(p => p.dataKey === "Total Jobs" || p.name === "Total Jobs");
  const subEntry = payload.find(p => p.dataKey === "Submissions" || p.name === "Submissions");

  const totalJobs = jobsEntry?.value ?? 0;
  const subVal = subEntry?.value ?? 0;
  const ratio = totalJobs > 0 ? (subVal / totalJobs).toFixed(1) : subVal > 0 ? subVal : "0.0";

  return (
    <div className="jcr-custom-tooltip">
      <div className="jcr-tooltip-header">
        <span className="jcr-tooltip-title">📅 {label}</span>
      </div>
      <div className="jcr-tooltip-body">
        <div className="jcr-tooltip-row">
          <div className="jcr-tooltip-indicator" style={{ background: "#6366f1" }}></div>
          <span className="jcr-tooltip-label">Total Jobs:</span>
          <span className="jcr-tooltip-val" style={{ color: "#4f46e5" }}>{totalJobs}</span>
        </div>
        <div className="jcr-tooltip-row">
          <div className="jcr-tooltip-indicator" style={{ background: "#3b82f6" }}></div>
          <span className="jcr-tooltip-label">Submissions:</span>
          <span className="jcr-tooltip-val" style={{ color: "#2563eb" }}>{subVal}</span>
        </div>
        <div className="jcr-tooltip-ratio-pill">
          ⚡ <strong>{ratio}</strong> Submissions / Job
        </div>
      </div>
    </div>
  );
};

// ─── Custom Tooltip: Job Creation Source (Bottom Chart) ──────────────────────
const JobSourceTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  const autoEntry = payload.find(p => p.dataKey === "Automation Jobs" || p.name === "Automation Jobs");
  const manualEntry = payload.find(p => p.dataKey === "Manual Jobs" || p.name === "Manual Jobs");

  const autoVal = autoEntry?.value ?? 0;
  const manualVal = manualEntry?.value ?? 0;
  const total = autoVal + manualVal;
  const autoPercent = total > 0 ? Math.round((autoVal / total) * 100) : 0;

  return (
    <div className="jcr-custom-tooltip">
      <div className="jcr-tooltip-header">
        <span className="jcr-tooltip-title">📅 {label}</span>
      </div>
      <div className="jcr-tooltip-body">
        <div className="jcr-tooltip-row">
          <div className="jcr-tooltip-indicator" style={{ background: "#3b82f6" }}></div>
          <span className="jcr-tooltip-label">Automation Jobs:</span>
          <span className="jcr-tooltip-val" style={{ color: "#2563eb" }}>{autoVal}</span>
        </div>
        <div className="jcr-tooltip-row">
          <div className="jcr-tooltip-indicator" style={{ background: "#8b5cf6" }}></div>
          <span className="jcr-tooltip-label">Manual Jobs:</span>
          <span className="jcr-tooltip-val" style={{ color: "#7c3aed" }}>{manualVal}</span>
        </div>
        <div className="jcr-tooltip-row" style={{ borderTop: "1px dashed #e2e8f0", paddingTop: "4px", marginTop: "2px" }}>
          <div className="jcr-tooltip-indicator" style={{ background: "#64748b" }}></div>
          <span className="jcr-tooltip-label" style={{ fontWeight: 600, color: "#334155" }}>Total Created:</span>
          <span className="jcr-tooltip-val" style={{ color: "#334155" }}>{total}</span>
        </div>
        <div className="jcr-tooltip-ratio-pill" style={{ background: "#eff6ff", borderColor: "#bfdbfe", color: "#1e40af" }}>
          🤖 <strong>{autoPercent}%</strong> Automation Share
        </div>
      </div>
    </div>
  );
};

const toYMD = (d) => {
  if (!d) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const JobCountReport = () => {
  const todayVal = toYMD(new Date());
  const initialFilters = {
    startDate: todayVal,
    endDate: todayVal,
    dropdownSelection: "today",
    trendPeriod: "daily",
    creationType: "all",
    regionFilter: "onsite",
    selectedClient: null,
    activePeriod: null,
    chartType: "area",
    clientSubFilter: "all"
  };

  const { filters, setFilters, handleClearFilter } = usePersistentFilters("jobCountReportFilters2", initialFilters);
  const startDate         = filters.startDate         || todayVal;
  const endDate           = filters.endDate           || todayVal;
  const dropdownSelection = filters.dropdownSelection || "today";
  const trendPeriod       = filters.trendPeriod       || "daily";
  const creationType      = filters.creationType      || "all";
  const regionFilter      = filters.regionFilter      || "onsite";
  const selectedClient    = filters.selectedClient    || null;
  const activePeriod      = filters.activePeriod      || null;
  const chartType         = filters.chartType         || "area";
  const clientSubFilter   = filters.clientSubFilter   || "all";

  const setStartDate      = (v) => setFilters(p => ({ ...p, startDate: v }));
  const setEndDate        = (v) => setFilters(p => ({ ...p, endDate: v }));
  const setDropdownSel    = (v) => setFilters(p => ({ ...p, dropdownSelection: v }));
  const setTrendPeriod    = (v) => setFilters(p => ({ ...p, trendPeriod: v }));
  const setCreationType   = (v) => setFilters(p => ({ ...p, creationType: v }));
  const setRegionFilter   = (v) => setFilters(p => ({ ...p, regionFilter: v }));
  const setSelectedClient = (v) => setFilters(p => ({ ...p, selectedClient: typeof v === 'function' ? v(p.selectedClient) : v }));
  const setActivePeriod   = (v) => setFilters(p => ({ ...p, activePeriod: typeof v === 'function' ? v(p.activePeriod) : v }));
  const setChartType      = (v) => setFilters(p => ({ ...p, chartType: v }));
  const setClientSubFilter = (v) => setFilters(p => ({ ...p, clientSubFilter: typeof v === 'function' ? v(p.clientSubFilter || "all") : v }));

  const [clientOptions, setClientOptions] = useState([]);
  const [missingModalClient, setMissingModalClient] = useState(null); // client name string or 'all' or null
  const [isReportDropdownOpen, setIsReportDropdownOpen] = useState(false);
  const reportDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (reportDropdownRef.current && !reportDropdownRef.current.contains(e.target)) {
        setIsReportDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDownloadReport = async (reportType) => {
    setIsReportDropdownOpen(false);

    if (reportType === 'standard') {
      generateJobReportHtml({
        startDate,
        endDate,
        regionFilter,
        creationType,
        selectedClient,
        clientSubFilter,
        displayedTotals,
        filteredClientBreakdown,
        byWorkMode: breakdowns.byWorkMode
      });
      return;
    }

    const today = new Date();
    let repStart = startDate;
    let repEnd = endDate;

    if (reportType === 'today') {
      repStart = toYMD(today);
      repEnd = toYMD(today);
    } else if (reportType === 'weekly') {
      const day = today.getDay();
      const diff = today.getDate() - day + (day === 0 ? -6 : 1);
      const startOfWeek = new Date(today.getFullYear(), today.getMonth(), diff);
      const endOfWeek = new Date(today.getFullYear(), today.getMonth(), diff + 4);
      repStart = toYMD(startOfWeek);
      repEnd = toYMD(endOfWeek);
    } else if (reportType === 'monthly') {
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      repStart = toYMD(startOfMonth);
      repEnd = toYMD(today);
    } else if (reportType === 'custom') {
      repStart = startDate;
      repEnd = endDate;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BASE_URL}/api/recruitment/applications/job-report/executive-data`, {
        params: {
          startDate: repStart,
          endDate: repEnd,
          region: regionFilter,
          client: selectedClient || 'all'
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      const execData = res.data || {};
      generateExecutiveDeckHtml({
        startDate: repStart,
        endDate: repEnd,
        regionFilter,
        selectedClient: selectedClient || 'All Clients',
        summary: execData.summary || {
          totalOpenJobs: displayedTotals.jobCount || 0,
          totalSubmissions: displayedTotals.submissionCount || 0,
          totalInterviews: 0,
          totalClosures: 0
        },
        podData: execData.podData || [],
        keyClosures: execData.keyClosures || [],
        interviewsAwaiting: execData.interviewsAwaiting || [],
        onboardingPipeline: execData.onboardingPipeline || {}
      });
    } catch (err) {
      console.warn("Could not fetch executive data, fallback:", err);
      generateExecutiveDeckHtml({
        startDate: repStart,
        endDate: repEnd,
        regionFilter,
        selectedClient: selectedClient || 'All Clients',
        summary: {
          totalOpenJobs: displayedTotals.jobCount || 0,
          totalSubmissions: displayedTotals.submissionCount || 0,
          totalInterviews: 0,
          totalClosures: 0
        },
        podData: []
      });
    }
  };

  // Interactive legend state for Chart 1 (Total Jobs & Submissions)
  const [activeSeriesJobs, setActiveSeriesJobs] = useState({
    "Total Jobs": true,
    "Submissions": true
  });

  const handleLegendClickJobs = (dataKey) => {
    setActiveSeriesJobs(prev => {
      const allKeys = ["Total Jobs", "Submissions"];
      const otherKeys = allKeys.filter(k => k !== dataKey);
      const isOnlyThisActive = prev[dataKey] && otherKeys.every(k => !prev[k]);

      if (isOnlyThisActive) {
        return {
          "Total Jobs": true,
          "Submissions": true
        };
      } else {
        return {
          "Total Jobs": dataKey === "Total Jobs",
          "Submissions": dataKey === "Submissions"
        };
      }
    });
  };

  // State and interactive legend for Chart 2 (Job Creation Source: Automation vs. Manual)
  const [chartTypeSource, setChartTypeSource] = useState("area");
  const [activeSeriesSource, setActiveSeriesSource] = useState({
    "Automation Jobs": true,
    "Manual Jobs": true
  });

  const handleLegendClickSource = (dataKey) => {
    setActiveSeriesSource(prev => {
      const allKeys = ["Automation Jobs", "Manual Jobs"];
      const otherKeys = allKeys.filter(k => k !== dataKey);
      const isOnlyThisActive = prev[dataKey] && otherKeys.every(k => !prev[k]);

      if (isOnlyThisActive) {
        return {
          "Automation Jobs": true,
          "Manual Jobs": true
        };
      } else {
        return {
          "Automation Jobs": dataKey === "Automation Jobs",
          "Manual Jobs": dataKey === "Manual Jobs"
        };
      }
    });
  };

  const [summary, setSummary] = useState({
    jobsToday: 0,       jobsTodayOnsite: 0,       jobsTodayOffshore: 0,
    jobsYesterday: 0,   jobsYesterdayOnsite: 0,   jobsYesterdayOffshore: 0,
    jobsThisMonth: 0,   jobsThisMonthOnsite: 0,   jobsThisMonthOffshore: 0,
    submissionsToday: 0,     submissionsTodayOnsite: 0,     submissionsTodayOffshore: 0,
    submissionsYesterday: 0, submissionsYesterdayOnsite: 0, submissionsYesterdayOffshore: 0,
    submissionsThisMonth: 0, submissionsThisMonthOnsite: 0, submissionsThisMonthOffshore: 0,
    selectedJobTotal: 0, selectedJobOnsite: 0, selectedJobOffshore: 0,
    selectedSubmissionTotal: 0, selectedSubmissionOnsite: 0, selectedSubmissionOffshore: 0,
    selectedJobsWithSubsTotal: 0, selectedJobsWithSubsOnsite: 0, selectedJobsWithSubsOffshore: 0,
    selectedJobsWithoutSubsTotal: 0, selectedJobsWithoutSubsOnsite: 0, selectedJobsWithoutSubsOffshore: 0
  });
  const [breakdowns, setBreakdowns] = useState({ byClient: [], byWorkMode: [] });
  const [trendData,  setTrendData]  = useState([]);
  const [isLoading,  setIsLoading]  = useState(true);
  const [error,      setError]      = useState("");

  const now = new Date();
  const thisMonthLabel = MONTHS[now.getMonth()] + " " + now.getFullYear();

  // Helper: compute displayed value from summary based on regionFilter
  const getVal = (total, onsite, offshore) => {
    const rf = regionFilter || "onsite";
    if (rf === "onsite")  return onsite;
    if (rf === "offshore") return offshore;
    return total;
  };

  const fetchReportData = useCallback(async (oStart, oEnd, oTp, oCr, oClient, oRegion) => {
    const sDate  = oStart  !== undefined ? oStart  : startDate;
    const eDate  = oEnd    !== undefined ? oEnd    : endDate;
    const tp     = oTp     !== undefined ? oTp     : trendPeriod;
    const cr     = oCr     !== undefined ? oCr     : creationType;
    const clientObj = oClient !== undefined ? oClient : selectedClient;
    const cl = clientObj ? clientObj.name : "";
    const reg = oRegion !== undefined ? oRegion : regionFilter;

    try {
      setIsLoading(true);
      setError("");
      const token = localStorage.getItem("token");
      const params = { startDate: sDate, endDate: eDate, trendPeriod: tp, creationType: cr, client: cl, region: reg };
      const res = await axios.get(BASE_URL + "/api/jobs/stats/counts", {
        headers: { Authorization: "Bearer " + token },
        params
      });
      if (res.data.success) {
        setSummary(res.data.summary || {});
        setBreakdowns(res.data.breakdowns || { byClient: [], byWorkMode: [] });
        setTrendData(res.data.trendData || []);
      }
    } catch (err) {
      console.error("Error fetching job report counts:", err);
      setError("Failed to load job report data.");
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate, trendPeriod, creationType, selectedClient, regionFilter]);

  // Fetch client options for the dropdown
  useEffect(() => {
    const fetchClientOptions = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(BASE_URL + "/api/jobs/stats/client-counts", {
          headers: { Authorization: "Bearer " + token },
          params: { startDate, endDate, creationType, region: regionFilter }
        });
        if (res.data.success) {
          setClientOptions(res.data.clients || []);
        }
      } catch (err) {
        console.error("Error fetching client options:", err);
      }
    };
    fetchClientOptions();
  }, [startDate, endDate, creationType, regionFilter]);

  useEffect(() => {
    fetchReportData(startDate, endDate, trendPeriod, creationType, selectedClient, regionFilter);
    // eslint-disable-next-line
  }, [startDate, endDate, trendPeriod, creationType, selectedClient, regionFilter]);

  const handleDropdownChange = (e) => {
    const val = e.target.value;
    setDropdownSel(val);
    if (val === "custom") return;
    const today = new Date();
    let start = new Date(today);
    let end = new Date(today);

    if (val === "today") {
      start = new Date(today);
      end = new Date(today);
    } else if (val === "yesterday") {
      start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
      end = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
    } else if (val === "this_week") {
      const day = today.getDay();
      const diff = today.getDate() - day + (day === 0 ? -6 : 1);
      start = new Date(today.getFullYear(), today.getMonth(), diff);
      end = new Date(today);
    } else if (val === "last_week") {
      const day = today.getDay();
      const diff = today.getDate() - day + (day === 0 ? -6 : 1) - 7;
      start = new Date(today.getFullYear(), today.getMonth(), diff);
      end = new Date(today.getFullYear(), today.getMonth(), diff + 6);
    } else if (val === "this_month") {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today);
    } else if (val === "last_month") {
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      end = new Date(today.getFullYear(), today.getMonth(), 0);
    } else if (val === "this_quarter") {
      const q = Math.floor(today.getMonth() / 3);
      start = new Date(today.getFullYear(), q * 3, 1);
      end = new Date(today);
    } else if (val === "last_quarter") {
      const q = Math.floor(today.getMonth() / 3) - 1;
      start = new Date(today.getFullYear(), q * 3, 1);
      end = new Date(today.getFullYear(), (q + 1) * 3, 0);
    }

    const newStart = toYMD(start);
    const newEnd   = toYMD(end);
    setStartDate(newStart); setEndDate(newEnd);
    fetchReportData(newStart, newEnd, trendPeriod);
  };

  const handlePeriodChange = (period) => {
    setActivePeriod(period);
    setTrendPeriod(period);
    const today = new Date();
    let start = new Date(today);

    if (period === "daily")        start.setDate(today.getDate() - 30);
    else if (period === "weekly")  start.setMonth(today.getMonth() - 3);
    else if (period === "monthly") start.setFullYear(today.getFullYear() - 1);
    else                           start.setFullYear(today.getFullYear() - 5);

    const sDate = toYMD(start);
    const eDate = toYMD(today);
    setStartDate(sDate); setEndDate(eDate); setDropdownSel("custom");
    fetchReportData(sDate, eDate, period);
  };

  const chartData = useMemo(() => {
    if (!trendData || trendData.length === 0) return [];
    const rf = regionFilter || "onsite";
    return trendData.map(item => {
      const autoVal = rf === "onsite" ? (item.jobCountAutomationOnsite ?? 0) : rf === "offshore" ? (item.jobCountAutomationOffshore ?? 0) : (item.jobCountAutomation ?? 0);
      const manualVal = rf === "onsite" ? (item.jobCountManualOnsite ?? 0) : rf === "offshore" ? (item.jobCountManualOffshore ?? 0) : (item.jobCountManual ?? 0);
      const subVal = rf === "onsite" ? (item.subCountOnsite ?? 0) : rf === "offshore" ? (item.subCountOffshore ?? 0) : (item.subCount ?? 0);
      const totalJobs = autoVal + manualVal;

      return {
        name: formatDateKey(item.dateKey, trendPeriod),
        "Total Jobs": totalJobs,
        "Submissions": subVal,
        "Automation Jobs": autoVal,
        "Manual Jobs": manualVal
      };
    });
  }, [trendData, trendPeriod, regionFilter]);

  // ─── Displayed Totals for Top Stat Cards (filters to selected client if one is selected) ───
  const displayedTotals = useMemo(() => {
    const totalJobs = getVal(summary.selectedJobTotal, summary.selectedJobOnsite, summary.selectedJobOffshore) ?? 0;
    const totalSubs = getVal(summary.selectedSubmissionTotal, summary.selectedSubmissionOnsite, summary.selectedSubmissionOffshore) ?? 0;
    const jobsWithSubs = getVal(summary.selectedJobsWithSubsTotal, summary.selectedJobsWithSubsOnsite, summary.selectedJobsWithSubsOffshore) ?? 0;
    const jobsWithoutSubs = getVal(summary.selectedJobsWithoutSubsTotal, summary.selectedJobsWithoutSubsOnsite, summary.selectedJobsWithoutSubsOffshore) ?? 0;

    if (selectedClient) {
      const clientRow = (breakdowns.byClient || []).find(
        c => (c.clientName || '').toLowerCase() === selectedClient.name.toLowerCase()
      );
      const cJobCount = clientRow ? (clientRow.jobCount ?? totalJobs) : totalJobs;
      const cSubCount = clientRow ? (clientRow.submissionCount ?? totalSubs) : totalSubs;
      return {
        clientCount: clientRow ? 1 : 0,
        clientSub: `Selected: ${selectedClient.name}`,
        jobCount: cJobCount,
        jobSub: `Openings for ${selectedClient.name}`,
        submissionCount: cSubCount,
        subSub: `Submissions for ${selectedClient.name}`,
        jobsWithSubs: jobsWithSubs,
        jobsWithoutSubs: jobsWithoutSubs
      };
    }
    const totalClients = (breakdowns.byClient || []).length;
    return {
      clientCount: totalClients,
      clientSub: "Unique clients in period",
      jobCount: totalJobs,
      jobSub: "Job openings in period",
      submissionCount: totalSubs,
      subSub: "Submissions across all clients",
      jobsWithSubs: jobsWithSubs,
      jobsWithoutSubs: jobsWithoutSubs
    };
  }, [selectedClient, breakdowns.byClient, summary, regionFilter]);

  // ─── Filtered Client Breakdown (All / With Submissions / Without Submissions) ───
  const clientsWithSubsCount = useMemo(() => (breakdowns.byClient || []).filter(c => (c.submissionCount || 0) > 0).length, [breakdowns.byClient]);
  const clientsWithoutSubsCount = useMemo(() => (breakdowns.byClient || []).filter(c => (c.submissionCount || 0) === 0).length, [breakdowns.byClient]);

  const filteredClientBreakdown = useMemo(() => {
    const list = breakdowns.byClient || [];
    if (clientSubFilter === "with_subs") {
      return list.filter(c => (c.submissionCount || 0) > 0);
    }
    if (clientSubFilter === "without_subs") {
      return list.filter(c => (c.submissionCount || 0) === 0);
    }
    return list;
  }, [breakdowns.byClient, clientSubFilter]);

  const isFiltered = dropdownSelection !== "today" || 
    startDate !== todayVal || 
    endDate !== todayVal || 
    creationType !== "all" || 
    regionFilter !== "onsite" || 
    selectedClient !== null ||
    activePeriod !== null;

  // Region toggle button style helper (pill button matching Submissions / Job Submission Report)
  const regionBtnStyle = (r) => {
    const active = regionFilter === r;
    return {
      padding: "6px 16px",
      borderRadius: "20px",
      border: active ? "1px solid #3b5bfd" : "1px solid #e2e8f0",
      background: active ? "#3b5bfd" : "#fff",
      color: active ? "#fff" : "#64748b",
      fontSize: "13px",
      fontWeight: "500",
      cursor: "pointer",
      transition: "all 0.2s ease"
    };
  };

  return (
    <div className="jcr-container">
      {/* Top Header */}
      <div className="jcr-header-section" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <h2 className="jcr-title">Job Report</h2>
        </div>
        {/* Onsite / Offshore Toggle in Top Corner */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <button style={regionBtnStyle("onsite")} onClick={() => setRegionFilter("onsite")}>Onsite</button>
          <button style={regionBtnStyle("offshore")} onClick={() => setRegionFilter("offshore")}>Offshore</button>
        </div>
      </div>

      {/* Date & Filter Bar */}
      <div className="jcr-filters">
        <div className="jcr-filter-label">Date filters:</div>
        <div className="jcr-filter-row">
          <div className="jcr-input-group">
            <label>Quick Select</label>
            <select value={dropdownSelection} onChange={handleDropdownChange}>
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="this_week">This Week</option>
              <option value="last_week">Last Week</option>
              <option value="this_month">This Month</option>
              <option value="last_month">Last Month</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>
          <div className="jcr-input-group">
            <label>Start date</label>
            <input type="date" value={startDate} onChange={(e)=>{ setStartDate(e.target.value); setDropdownSel("custom"); }} />
          </div>
          <div className="jcr-input-group">
            <label>End date</label>
            <input type="date" value={endDate} onChange={(e)=>{ setEndDate(e.target.value); setDropdownSel("custom"); }} />
          </div>
          <button className="jcr-apply-btn" onClick={()=>fetchReportData()} disabled={isLoading}>
            {isLoading ? "Loading..." : "Apply"}
          </button>

          {/* Searchable Client Filter Dropdown */}
          <SearchableClientSelect
            clients={clientOptions.length > 0 ? clientOptions : (breakdowns.byClient || [])}
            selectedClient={selectedClient}
            onSelect={(name) => {
              const newClient = name ? { name } : null;
              setSelectedClient(newClient);
              fetchReportData(startDate, endDate, trendPeriod, creationType, newClient);
            }}
          />

          {/* Creation Source Dropdown (Automation vs Manual) */}
          <div className="jcr-input-group">
            <label>Creation Source</label>
            <select 
              value={creationType} 
              onChange={(e) => {
                const val = e.target.value;
                setCreationType(val);
                fetchReportData(startDate, endDate, trendPeriod, val);
              }}
            >
              <option value="all">All (Automation &amp; Manual)</option>
              <option value="automation">Automation</option>
              <option value="manual">Manual</option>
            </select>
          </div>

          {/* Download Report Dropdown */}
          <div style={{ position: 'relative' }} ref={reportDropdownRef}>
            <button
              onClick={() => setIsReportDropdownOpen(prev => !prev)}
              title="Download Report Options"
              style={{
                padding: "6px 14px",
                borderRadius: "6px",
                border: "1px solid #3b5bfd",
                background: isReportDropdownOpen ? "#3b5bfd" : "#ffffff",
                color: isReportDropdownOpen ? "#ffffff" : "#3b5bfd",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: "6px",
                height: "36px",
                alignSelf: "flex-end",
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                transition: "all 0.15s ease"
              }}
              onMouseEnter={(e) => { if (!isReportDropdownOpen) e.currentTarget.style.background = "#eff6ff"; }}
              onMouseLeave={(e) => { if (!isReportDropdownOpen) e.currentTarget.style.background = "#ffffff"; }}
            >
              <Download size={14} /> Download Report <ChevronDown size={14} style={{ transform: isReportDropdownOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
            </button>

            {isReportDropdownOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '6px',
                background: '#ffffff',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 10px 25px -5px rgba(0,0,0,0.15), 0 8px 10px -6px rgba(0,0,0,0.1)',
                zIndex: 1000,
                minWidth: '240px',
                overflow: 'hidden'
              }}>
                <div style={{ padding: '8px 12px', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', background: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                  Select Report Format
                </div>

                <button
                  type="button"
                  onClick={() => handleDownloadReport('today')}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'none',
                    border: 'none',
                    textAlign: 'left',
                    fontSize: '13px',
                    color: '#1e293b',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f8fafc',
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#3b5bfd'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#1e293b'; }}
                >
                  <span>⚡</span>
                  <div>
                    <div style={{ fontWeight: '600' }}>Today</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>Single day pipeline review</div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => handleDownloadReport('weekly')}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'none',
                    border: 'none',
                    textAlign: 'left',
                    fontSize: '13px',
                    color: '#0f172a',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f8fafc',
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#3b5bfd'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#0f172a'; }}
                >
                  <span>📊</span>
                  <div>
                    <div style={{ fontWeight: '700', color: '#3b5bfd' }}>Weekly (Executive Deck)</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>9-slide PPTX-style review (Mon–Fri)</div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => handleDownloadReport('monthly')}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'none',
                    border: 'none',
                    textAlign: 'left',
                    fontSize: '13px',
                    color: '#1e293b',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f8fafc',
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#3b5bfd'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#1e293b'; }}
                >
                  <span>🗓️</span>
                  <div>
                    <div style={{ fontWeight: '600' }}>Monthly</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>Current month performance</div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => handleDownloadReport('custom')}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'none',
                    border: 'none',
                    textAlign: 'left',
                    fontSize: '13px',
                    color: '#1e293b',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f8fafc',
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#3b5bfd'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#1e293b'; }}
                >
                  <span>⚙️</span>
                  <div>
                    <div style={{ fontWeight: '600' }}>Selected Date Range</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>{startDate} — {endDate}</div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => handleDownloadReport('standard')}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'none',
                    border: 'none',
                    textAlign: 'left',
                    fontSize: '13px',
                    color: '#1e293b',
                    cursor: 'pointer',
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#3b5bfd'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#1e293b'; }}
                >
                  <span>📑</span>
                  <div>
                    <div style={{ fontWeight: '600' }}>Standard Job Report</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>Complete job opening & client analytics (PDF/Print)</div>
                  </div>
                </button>
              </div>
            )}
          </div>

          {isFiltered && (
            <button 
              onClick={() => { 
                handleClearFilter(); 
                setStartDate(todayVal); 
                setEndDate(todayVal); 
                setDropdownSel("today"); 
                setTrendPeriod("daily"); 
                setCreationType("all"); 
                setRegionFilter("onsite"); 
                setSelectedClient(null); 
                setActivePeriod(null); 
                fetchReportData(todayVal, todayVal, "daily", "all", null); 
              }}
              style={{
                padding: "6px 14px",
                borderRadius: "6px",
                border: "1px solid #ef4444",
                background: "#fef2f2",
                color: "#ef4444",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: "6px",
                height: "36px",
                alignSelf: "flex-end"
              }}
            >
              <RotateCcw size={14}/> Clear All Filters
            </button>
          )}
        </div>
      </div>

      {error && <p style={{color:"#ef4444",marginBottom:16}}>{error}</p>}

      {/* ── Stat Cards ── */}
      <div className="jcr-stats-grid">
        <div className="jcr-stat-card clients">
          <div className="jcr-card-top">
            <span className="jcr-card-label">Total Clients</span>
            <span className="jcr-card-icon"><Building2 size={16}/></span>
          </div>
          <div className="jcr-card-number">{isLoading ? "…" : (displayedTotals.clientCount ?? 0)}</div>
          <div className="jcr-card-sub">{displayedTotals.clientSub}</div>
        </div>

        <div className="jcr-stat-card jobs">
          <div className="jcr-card-top">
            <span className="jcr-card-label">Total Jobs</span>
            <span className="jcr-card-icon"><Briefcase size={16}/></span>
          </div>
          <div className="jcr-card-number">{isLoading ? "…" : (displayedTotals.jobCount ?? 0)}</div>
          <div className="jcr-card-sub">{displayedTotals.jobSub}</div>
        </div>

        <div className="jcr-stat-card with-subs">
          <div className="jcr-card-top">
            <span className="jcr-card-label" style={{ color: "#2563eb" }}>Jobs With Subs</span>
            <span className="jcr-card-icon"><CheckCircle size={16}/></span>
          </div>
          <div className="jcr-card-number" style={{ color: "#2563eb" }}>
            {isLoading ? "…" : (displayedTotals.jobsWithSubs ?? 0)}
          </div>
          <div className="jcr-card-sub" style={{ color: "#2563eb", fontWeight: 600 }}>
            {displayedTotals.jobCount > 0 
              ? `${Math.round(((displayedTotals.jobsWithSubs || 0) / displayedTotals.jobCount) * 100)}% Coverage` 
              : "Active submissions"}
          </div>
        </div>

        <div 
          className="jcr-stat-card without-subs" 
          onClick={() => setMissingModalClient(selectedClient ? selectedClient.name : 'all')}
          title="Click to view all jobs missing candidate submissions"
        >
          <div className="jcr-card-top">
            <span className="jcr-card-label" style={{ color: "#991b1b" }}>⚠️ Missing Subs</span>
            <span className="jcr-card-icon"><AlertTriangle size={16}/></span>
          </div>
          <div className="jcr-card-number" style={{ color: "#dc2626" }}>
            {isLoading ? "…" : (displayedTotals.jobsWithoutSubs ?? 0)}
          </div>
          <div className="jcr-card-sub" style={{ color: "#b91c1c", display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: 600 }}>
            <span>0 Submissions</span>
            <span style={{ fontSize: "11px", textDecoration: "underline" }}>View Jobs ↗</span>
          </div>
        </div>

        <div className="jcr-stat-card subs">
          <div className="jcr-card-top">
            <span className="jcr-card-label">Total Submissions</span>
            <span className="jcr-card-icon"><FileText size={16}/></span>
          </div>
          <div className="jcr-card-number">{isLoading ? "…" : (displayedTotals.submissionCount ?? 0)}</div>
          <div className="jcr-card-sub">{displayedTotals.subSub}</div>
        </div>
      </div>

      {/* ── Dynamic Trend Chart Section 1: Total Jobs & Submissions ── */}
      <div className="jcr-chart-section">
        <div className="jcr-chart-header-row">
          <div className="jcr-chart-header-banner">
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '6px', background: '#e0e7ff', color: '#4f46e5' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline></svg>
            </span>
            <span>Job Count &amp; Submissions</span>{activePeriod ? ` — ${activePeriod.charAt(0).toUpperCase() + activePeriod.slice(1)} View` : ''}
          </div>
          
          <div className="jcr-chart-controls-group">
            {/* Chart Style Switcher */}
            <div className="jcr-chart-type-btns">
              <button 
                type="button" 
                title="Smooth Gradient Area Chart" 
                className={"jcr-chart-type-btn" + (chartType === "area" ? " active" : "")} 
                onClick={() => setChartType("area")}
              >
                <TrendingUp size={13} /> Area
              </button>
              <button 
                type="button" 
                title="Line Trend Chart" 
                className={"jcr-chart-type-btn" + (chartType === "line" ? " active" : "")} 
                onClick={() => setChartType("line")}
              >
                <Activity size={13} /> Line
              </button>
              <button 
                type="button" 
                title="Combo Bar & Line Chart" 
                className={"jcr-chart-type-btn" + (chartType === "combo" ? " active" : "")} 
                onClick={() => setChartType("combo")}
              >
                <Layers size={13} /> Combo
              </button>
              <button 
                type="button" 
                title="Classic Bar Chart" 
                className={"jcr-chart-type-btn" + (chartType === "bar" ? " active" : "")} 
                onClick={() => setChartType("bar")}
              >
                <BarChart2 size={13} /> Bar
              </button>
            </div>

            {/* Period Switcher */}
            <div className="jcr-period-btns">
              {["daily","weekly","monthly","yearly"].map(p=>(
                <button key={p} className={"jcr-period-btn"+(activePeriod===p?" active":"")} onClick={()=>handlePeriodChange(p)}>
                  {p.charAt(0).toUpperCase()+p.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div style={{padding:"20px",height:"320px"}}>
          {isLoading ? (<p className="jcr-loading">Loading chart...</p>)
          : chartData.length===0 ? (<p className="jcr-empty">No trend data for this period. Click a period button (Daily / Weekly / Monthly / Yearly) to expand the date range.</p>)
          : (
            <ResponsiveContainer width="100%" height="100%">
              {chartType === "area" ? (
                <AreaChart data={chartData} margin={{top:25,right:30,left:-10,bottom:5}}>
                  <defs>
                    <linearGradient id="jcrTotalJobsGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.45} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="jcrSubGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.45} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9"/>
                  <XAxis dataKey="name" tick={{fill:"#64748b",fontSize:11}} axisLine={{stroke:"#e2e8f0"}} tickLine={false}/>
                  <YAxis allowDecimals={false} tick={{fill:"#64748b",fontSize:11}} axisLine={false} tickLine={false}/>
                  <Tooltip content={<JobsAndSubsTooltip />} cursor={{stroke:"#cbd5e1",strokeDasharray:"4 4"}}/>
                  <Legend 
                    iconType="circle" 
                    wrapperStyle={{ paddingTop: "12px", fontSize: "13px", cursor: "pointer" }}
                    onClick={(e) => e && e.dataKey && handleLegendClickJobs(e.dataKey)}
                    formatter={(value, entry) => {
                      const isVisible = activeSeriesJobs[entry.dataKey];
                      return (
                        <span style={{
                          color: isVisible ? "#1e293b" : "#94a3b8",
                          opacity: isVisible ? 1 : 0.3,
                          cursor: "pointer",
                          fontWeight: isVisible ? 600 : 400,
                          marginRight: "16px",
                          userSelect: "none"
                        }}>
                          {value}
                        </span>
                      );
                    }}
                  />
                  <Area 
                    hide={!activeSeriesJobs["Total Jobs"]} 
                    type="monotone" 
                    dataKey="Total Jobs" 
                    stroke="#3b82f6" 
                    strokeWidth={2.5} 
                    fillOpacity={1} 
                    fill="url(#jcrTotalJobsGradient)" 
                    activeDot={{ r: 6, stroke: "#ffffff", strokeWidth: 2 }}
                  >
                    <LabelList dataKey="Total Jobs" position="top" offset={8} style={{ fill: '#4f46e5', fontSize: 11, fontWeight: 700 }} formatter={(v) => (v > 0 ? v : '')} />
                  </Area>
                  <Area 
                    hide={!activeSeriesJobs["Submissions"]} 
                    type="monotone" 
                    dataKey="Submissions" 
                    stroke="#3b82f6" 
                    strokeWidth={2.5} 
                    fillOpacity={1} 
                    fill="url(#jcrSubGradient)" 
                    activeDot={{ r: 6, stroke: "#ffffff", strokeWidth: 2 }}
                  >
                    <LabelList dataKey="Submissions" position="top" offset={8} style={{ fill: '#2563eb', fontSize: 11, fontWeight: 700 }} formatter={(v) => (v > 0 ? v : '')} />
                  </Area>
                </AreaChart>
              ) : chartType === "line" ? (
                <LineChart data={chartData} margin={{top:25,right:30,left:-10,bottom:5}}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9"/>
                  <XAxis dataKey="name" tick={{fill:"#64748b",fontSize:11}} axisLine={{stroke:"#e2e8f0"}} tickLine={false}/>
                  <YAxis allowDecimals={false} tick={{fill:"#64748b",fontSize:11}} axisLine={false} tickLine={false}/>
                  <Tooltip content={<JobsAndSubsTooltip />} cursor={{stroke:"#cbd5e1",strokeDasharray:"4 4"}}/>
                  <Legend 
                    iconType="circle" 
                    wrapperStyle={{ paddingTop: "12px", fontSize: "13px", cursor: "pointer" }}
                    onClick={(e) => e && e.dataKey && handleLegendClickJobs(e.dataKey)}
                    formatter={(value, entry) => {
                      const isVisible = activeSeriesJobs[entry.dataKey];
                      return (
                        <span style={{
                          color: isVisible ? "#1e293b" : "#94a3b8",
                          opacity: isVisible ? 1 : 0.3,
                          cursor: "pointer",
                          fontWeight: isVisible ? 600 : 400,
                          marginRight: "16px",
                          userSelect: "none"
                        }}>
                          {value}
                        </span>
                      );
                    }}
                  />
                  <Line 
                    hide={!activeSeriesJobs["Total Jobs"]} 
                    type="monotone" 
                    dataKey="Total Jobs" 
                    stroke="#3b82f6" 
                    strokeWidth={2.5} 
                    dot={{ r: 3, fill: "#6366f1" }} 
                    activeDot={{ r: 6, stroke: "#ffffff", strokeWidth: 2 }}
                  >
                    <LabelList dataKey="Total Jobs" position="top" offset={8} style={{ fill: '#4f46e5', fontSize: 11, fontWeight: 700 }} formatter={(v) => (v > 0 ? v : '')} />
                  </Line>
                  <Line 
                    hide={!activeSeriesJobs["Submissions"]} 
                    type="monotone" 
                    dataKey="Submissions" 
                    stroke="#3b82f6" 
                    strokeWidth={2.5} 
                    dot={{ r: 3, fill: "#3b82f6" }} 
                    activeDot={{ r: 6, stroke: "#ffffff", strokeWidth: 2 }}
                  >
                    <LabelList dataKey="Submissions" position="top" offset={8} style={{ fill: '#2563eb', fontSize: 11, fontWeight: 700 }} formatter={(v) => (v > 0 ? v : '')} />
                  </Line>
                </LineChart>
              ) : chartType === "combo" ? (
                <ComposedChart data={chartData} margin={{top:25,right:30,left:-10,bottom:5}}>
                  <defs>
                    <linearGradient id="jcrSubGradientCombo" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9"/>
                  <XAxis dataKey="name" tick={{fill:"#64748b",fontSize:11}} axisLine={{stroke:"#e2e8f0"}} tickLine={false}/>
                  <YAxis allowDecimals={false} tick={{fill:"#64748b",fontSize:11}} axisLine={false} tickLine={false}/>
                  <Tooltip content={<JobsAndSubsTooltip />} cursor={{fill:"#f8fafc"}}/>
                  <Legend 
                    iconType="circle" 
                    wrapperStyle={{ paddingTop: "12px", fontSize: "13px", cursor: "pointer" }}
                    onClick={(e) => e && e.dataKey && handleLegendClickJobs(e.dataKey)}
                    formatter={(value, entry) => {
                      const isVisible = activeSeriesJobs[entry.dataKey];
                      return (
                        <span style={{
                          color: isVisible ? "#1e293b" : "#94a3b8",
                          opacity: isVisible ? 1 : 0.3,
                          cursor: "pointer",
                          fontWeight: isVisible ? 600 : 400,
                          marginRight: "16px",
                          userSelect: "none"
                        }}>
                          {value}
                        </span>
                      );
                    }}
                  />
                  <Bar 
                    hide={!activeSeriesJobs["Total Jobs"]} 
                    dataKey="Total Jobs" 
                    fill="#3b82f6" 
                    radius={[4,4,0,0]} 
                    barSize={14} 
                    opacity={0.9}
                  >
                    <LabelList dataKey="Total Jobs" position="top" style={{ fill: '#4f46e5', fontSize: 11, fontWeight: 700 }} formatter={(v) => (v > 0 ? v : '')} />
                  </Bar>
                  <Area 
                    hide={!activeSeriesJobs["Submissions"]} 
                    type="monotone" 
                    dataKey="Submissions" 
                    stroke="#3b82f6" 
                    strokeWidth={2.5} 
                    fillOpacity={1} 
                    fill="url(#jcrSubGradientCombo)" 
                    activeDot={{ r: 6, stroke: "#ffffff", strokeWidth: 2 }}
                  >
                    <LabelList dataKey="Submissions" position="top" offset={8} style={{ fill: '#2563eb', fontSize: 11, fontWeight: 700 }} formatter={(v) => (v > 0 ? v : '')} />
                  </Area>
                </ComposedChart>
              ) : (
                <BarChart data={chartData} margin={{top:25,right:30,left:0,bottom:5}}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0"/>
                  <XAxis dataKey="name" tick={{fill:"#64748b",fontSize:12}} axisLine={false} tickLine={false}/>
                  <YAxis allowDecimals={false} tick={{fill:"#64748b",fontSize:12}} axisLine={false} tickLine={false}/>
                  <Tooltip content={<JobsAndSubsTooltip />} cursor={{fill:"#f8fafc"}}/>
                  <Legend 
                    iconType="square" 
                    wrapperStyle={{ paddingTop: "12px", fontSize: "13px", cursor: "pointer" }}
                    onClick={(e) => e && e.dataKey && handleLegendClickJobs(e.dataKey)}
                    formatter={(value, entry) => {
                      const isVisible = activeSeriesJobs[entry.dataKey];
                      return (
                        <span style={{
                          color: isVisible ? "#1e293b" : "#94a3b8",
                          opacity: isVisible ? 1 : 0.3,
                          cursor: "pointer",
                          fontWeight: isVisible ? 600 : 400,
                          marginRight: "16px",
                          userSelect: "none"
                        }}>
                          {value}
                        </span>
                      );
                    }}
                  />
                  <Bar hide={!activeSeriesJobs["Total Jobs"]} dataKey="Total Jobs" fill="#6366f1" radius={[4,4,0,0]} barSize={12}>
                    <LabelList dataKey="Total Jobs" position="top" style={{ fill: '#4f46e5', fontSize: 11, fontWeight: 700 }} formatter={(v) => (v > 0 ? v : '')} />
                  </Bar>
                  <Bar hide={!activeSeriesJobs["Submissions"]} dataKey="Submissions" fill="#3b82f6" radius={[4,4,0,0]} barSize={12}>
                    <LabelList dataKey="Submissions" position="top" style={{ fill: '#2563eb', fontSize: 11, fontWeight: 700 }} formatter={(v) => (v > 0 ? v : '')} />
                  </Bar>
                </BarChart>
              )}
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* ── Dynamic Trend Chart Section 2: Job Creation Source (Automation vs Manual) ── */}
      <div className="jcr-chart-section">
        <div className="jcr-chart-header-row">
          <div className="jcr-chart-header-banner" style={{ background: "#f8fafc", color: "#1e293b", borderBottom: "1px solid #e2e8f0" }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '6px', background: '#e0e7ff', color: '#4f46e5' }}>
              <Layers size={16} />
            </span>
            <span>Job Creation Source (Automation vs. Manual)</span>{activePeriod ? ` — ${activePeriod.charAt(0).toUpperCase() + activePeriod.slice(1)} View` : ''}
          </div>
          
          <div className="jcr-chart-controls-group">
            {/* Chart Style Switcher for Source Chart */}
            <div className="jcr-chart-type-btns">
              <button 
                type="button" 
                title="Smooth Gradient Area Chart" 
                className={"jcr-chart-type-btn" + (chartTypeSource === "area" ? " active" : "")} 
                onClick={() => setChartTypeSource("area")}
              >
                <TrendingUp size={13} /> Area
              </button>
              <button 
                type="button" 
                title="Line Trend Chart" 
                className={"jcr-chart-type-btn" + (chartTypeSource === "line" ? " active" : "")} 
                onClick={() => setChartTypeSource("line")}
              >
                <Activity size={13} /> Line
              </button>
              <button 
                type="button" 
                title="Combo Bar & Line Chart" 
                className={"jcr-chart-type-btn" + (chartTypeSource === "combo" ? " active" : "")} 
                onClick={() => setChartTypeSource("combo")}
              >
                <Layers size={13} /> Combo
              </button>
              <button 
                type="button" 
                title="Classic Bar Chart" 
                className={"jcr-chart-type-btn" + (chartTypeSource === "bar" ? " active" : "")} 
                onClick={() => setChartTypeSource("bar")}
              >
                <BarChart2 size={13} /> Bar
              </button>
            </div>
          </div>
        </div>

        <div style={{padding:"20px",height:"320px"}}>
          {isLoading ? (<p className="jcr-loading">Loading chart...</p>)
          : chartData.length===0 ? (<p className="jcr-empty">No trend data for this period.</p>)
          : (
            <ResponsiveContainer width="100%" height="100%">
              {chartTypeSource === "area" ? (
                <AreaChart data={chartData} margin={{top:25,right:30,left:-10,bottom:5}}>
                  <defs>
                    <linearGradient id="jcrAutoGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.38} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="jcrManualGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.38} />
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9"/>
                  <XAxis dataKey="name" tick={{fill:"#64748b",fontSize:11}} axisLine={{stroke:"#e2e8f0"}} tickLine={false}/>
                  <YAxis allowDecimals={false} tick={{fill:"#64748b",fontSize:11}} axisLine={false} tickLine={false}/>
                  <Tooltip content={<JobSourceTooltip />} cursor={{stroke:"#cbd5e1",strokeDasharray:"4 4"}}/>
                  <Legend 
                    iconType="circle" 
                    wrapperStyle={{ paddingTop: "12px", fontSize: "13px", cursor: "pointer" }}
                    onClick={(e) => e && e.dataKey && handleLegendClickSource(e.dataKey)}
                    formatter={(value, entry) => {
                      const isVisible = activeSeriesSource[entry.dataKey];
                      return (
                        <span style={{
                          color: isVisible ? "#1e293b" : "#94a3b8",
                          opacity: isVisible ? 1 : 0.3,
                          cursor: "pointer",
                          fontWeight: isVisible ? 600 : 400,
                          marginRight: "16px",
                          userSelect: "none"
                        }}>
                          {value}
                        </span>
                      );
                    }}
                  />
                  <Area 
                    hide={!activeSeriesSource["Automation Jobs"]} 
                    type="monotone" 
                    dataKey="Automation Jobs" 
                    stroke="#3b82f6" 
                    strokeWidth={2.5} 
                    fillOpacity={1} 
                    fill="url(#jcrAutoGradient)" 
                    activeDot={{ r: 6, stroke: "#ffffff", strokeWidth: 2 }}
                  >
                    <LabelList dataKey="Automation Jobs" position="top" offset={8} style={{ fill: '#2563eb', fontSize: 11, fontWeight: 700 }} formatter={(v) => (v > 0 ? v : '')} />
                  </Area>
                  <Area 
                    hide={!activeSeriesSource["Manual Jobs"]} 
                    type="monotone" 
                    dataKey="Manual Jobs" 
                    stroke="#8b5cf6" 
                    strokeWidth={2.5} 
                    fillOpacity={1} 
                    fill="url(#jcrManualGradient)" 
                    activeDot={{ r: 6, stroke: "#ffffff", strokeWidth: 2 }}
                  >
                    <LabelList dataKey="Manual Jobs" position="top" offset={8} style={{ fill: '#7c3aed', fontSize: 11, fontWeight: 700 }} formatter={(v) => (v > 0 ? v : '')} />
                  </Area>
                </AreaChart>
              ) : chartTypeSource === "line" ? (
                <LineChart data={chartData} margin={{top:25,right:30,left:-10,bottom:5}}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9"/>
                  <XAxis dataKey="name" tick={{fill:"#64748b",fontSize:11}} axisLine={{stroke:"#e2e8f0"}} tickLine={false}/>
                  <YAxis allowDecimals={false} tick={{fill:"#64748b",fontSize:11}} axisLine={false} tickLine={false}/>
                  <Tooltip content={<JobSourceTooltip />} cursor={{stroke:"#cbd5e1",strokeDasharray:"4 4"}}/>
                  <Legend 
                    iconType="circle" 
                    wrapperStyle={{ paddingTop: "12px", fontSize: "13px", cursor: "pointer" }}
                    onClick={(e) => e && e.dataKey && handleLegendClickSource(e.dataKey)}
                    formatter={(value, entry) => {
                      const isVisible = activeSeriesSource[entry.dataKey];
                      return (
                        <span style={{
                          color: isVisible ? "#1e293b" : "#94a3b8",
                          opacity: isVisible ? 1 : 0.3,
                          cursor: "pointer",
                          fontWeight: isVisible ? 600 : 400,
                          marginRight: "16px",
                          userSelect: "none"
                        }}>
                          {value}
                        </span>
                      );
                    }}
                  />
                  <Line 
                    hide={!activeSeriesSource["Automation Jobs"]} 
                    type="monotone" 
                    dataKey="Automation Jobs" 
                    stroke="#3b82f6" 
                    strokeWidth={2.5} 
                    dot={{ r: 3, fill: "#3b82f6" }} 
                    activeDot={{ r: 6, stroke: "#ffffff", strokeWidth: 2 }}
                  >
                    <LabelList dataKey="Automation Jobs" position="top" offset={8} style={{ fill: '#2563eb', fontSize: 11, fontWeight: 700 }} formatter={(v) => (v > 0 ? v : '')} />
                  </Line>
                  <Line 
                    hide={!activeSeriesSource["Manual Jobs"]} 
                    type="monotone" 
                    dataKey="Manual Jobs" 
                    stroke="#8b5cf6" 
                    strokeWidth={2.5} 
                    dot={{ r: 3, fill: "#8b5cf6" }} 
                    activeDot={{ r: 6, stroke: "#ffffff", strokeWidth: 2 }}
                  >
                    <LabelList dataKey="Manual Jobs" position="top" offset={8} style={{ fill: '#7c3aed', fontSize: 11, fontWeight: 700 }} formatter={(v) => (v > 0 ? v : '')} />
                  </Line>
                </LineChart>
              ) : chartTypeSource === "combo" ? (
                <ComposedChart data={chartData} margin={{top:25,right:30,left:-10,bottom:5}}>
                  <defs>
                    <linearGradient id="jcrManualComboGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.38} />
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9"/>
                  <XAxis dataKey="name" tick={{fill:"#64748b",fontSize:11}} axisLine={{stroke:"#e2e8f0"}} tickLine={false}/>
                  <YAxis allowDecimals={false} tick={{fill:"#64748b",fontSize:11}} axisLine={false} tickLine={false}/>
                  <Tooltip content={<JobSourceTooltip />} cursor={{fill:"#f8fafc"}}/>
                  <Legend 
                    iconType="circle" 
                    wrapperStyle={{ paddingTop: "12px", fontSize: "13px", cursor: "pointer" }}
                    onClick={(e) => e && e.dataKey && handleLegendClickSource(e.dataKey)}
                    formatter={(value, entry) => {
                      const isVisible = activeSeriesSource[entry.dataKey];
                      return (
                        <span style={{
                          color: isVisible ? "#1e293b" : "#94a3b8",
                          opacity: isVisible ? 1 : 0.3,
                          cursor: "pointer",
                          fontWeight: isVisible ? 600 : 400,
                          marginRight: "16px",
                          userSelect: "none"
                        }}>
                          {value}
                        </span>
                      );
                    }}
                  />
                  <Bar 
                    hide={!activeSeriesSource["Automation Jobs"]} 
                    dataKey="Automation Jobs" 
                    fill="#3b82f6" 
                    radius={[4,4,0,0]} 
                    barSize={14} 
                    opacity={0.9}
                  >
                    <LabelList dataKey="Automation Jobs" position="top" style={{ fill: '#2563eb', fontSize: 11, fontWeight: 700 }} formatter={(v) => (v > 0 ? v : '')} />
                  </Bar>
                  <Area 
                    hide={!activeSeriesSource["Manual Jobs"]} 
                    type="monotone" 
                    dataKey="Manual Jobs" 
                    stroke="#8b5cf6" 
                    strokeWidth={2.5} 
                    fillOpacity={1} 
                    fill="url(#jcrManualComboGradient)" 
                    activeDot={{ r: 6, stroke: "#ffffff", strokeWidth: 2 }}
                  >
                    <LabelList dataKey="Manual Jobs" position="top" offset={8} style={{ fill: '#7c3aed', fontSize: 11, fontWeight: 700 }} formatter={(v) => (v > 0 ? v : '')} />
                  </Area>
                </ComposedChart>
              ) : (
                <BarChart data={chartData} margin={{top:25,right:30,left:0,bottom:5}}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0"/>
                  <XAxis dataKey="name" tick={{fill:"#64748b",fontSize:12}} axisLine={false} tickLine={false}/>
                  <YAxis allowDecimals={false} tick={{fill:"#64748b",fontSize:12}} axisLine={false} tickLine={false}/>
                  <Tooltip content={<JobSourceTooltip />} cursor={{fill:"#f8fafc"}}/>
                  <Legend 
                    iconType="square" 
                    wrapperStyle={{ paddingTop: "12px", fontSize: "13px", cursor: "pointer" }}
                    onClick={(e) => e && e.dataKey && handleLegendClickSource(e.dataKey)}
                    formatter={(value, entry) => {
                      const isVisible = activeSeriesSource[entry.dataKey];
                      return (
                        <span style={{
                          color: isVisible ? "#1e293b" : "#94a3b8",
                          opacity: isVisible ? 1 : 0.3,
                          cursor: "pointer",
                          fontWeight: isVisible ? 600 : 400,
                          marginRight: "16px",
                          userSelect: "none"
                        }}>
                          {value}
                        </span>
                      );
                    }}
                  />
                  <Bar hide={!activeSeriesSource["Automation Jobs"]} dataKey="Automation Jobs" fill="#3b82f6" radius={[4,4,0,0]} barSize={12}>
                    <LabelList dataKey="Automation Jobs" position="top" style={{ fill: '#2563eb', fontSize: 11, fontWeight: 700 }} formatter={(v) => (v > 0 ? v : '')} />
                  </Bar>
                  <Bar hide={!activeSeriesSource["Manual Jobs"]} dataKey="Manual Jobs" fill="#8b5cf6" radius={[4,4,0,0]} barSize={12}>
                    <LabelList dataKey="Manual Jobs" position="top" style={{ fill: '#7c3aed', fontSize: 11, fontWeight: 700 }} formatter={(v) => (v > 0 ? v : '')} />
                  </Bar>
                </BarChart>
              )}
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Summary Table */}
      <div className="jcr-table-section">
        <div className="jcr-table-header">
          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '6px', background: '#e0e7ff', color: '#4f46e5' }}>
            <FileText size={16}/>
          </span>
          <span>Job &amp; Submission Summary Report ({startDate===endDate ? startDate : startDate+" to "+endDate})</span>
          {regionFilter && (
            <span style={{marginLeft:"12px",background:"#e2e8f0",color:"#334155",border:"1px solid #cbd5e1",borderRadius:"12px",padding:"2px 10px",fontSize:"12px",fontWeight:500}}>
              {regionFilter === "onsite" ? "🖥 Onsite" : "🌐 Offshore"}
            </span>
          )}
          {creationType !== "all" && (
            <span style={{marginLeft:"8px",background:"#e2e8f0",color:"#334155",border:"1px solid #cbd5e1",borderRadius:"12px",padding:"2px 10px",fontSize:"12px",fontWeight:500}}>
              {creationType === "automation" ? "🤖 Automation" : "👤 Manual"}
            </span>
          )}
        </div>
        <div style={{padding:"24px",display:"flex",flexDirection:"column",gap:"24px"}}>
          {isLoading ? (<p className="jcr-loading">Loading summary statistics...</p>) : (
            <>
              <div>
                <h4 style={{margin:"0 0 12px 0",fontSize:"14px",color:"#1e293b",fontWeight:600}}>Period Summary</h4>
                <table className="jcr-table">
                  <thead><tr>
                    <th>Time Period</th>
                    <th style={{textAlign:"center"}}>Job Count</th>
                    <th style={{textAlign:"center"}}>Submission Count</th>
                  </tr></thead>
                  <tbody>
                    <tr style={{background:"#f8fafc"}}>
                      <td><strong>Selected Period ({startDate===endDate ? startDate : startDate+" to "+endDate})</strong></td>
                      <td style={{textAlign:"center",fontWeight:700,color:"#1e293b",fontSize:"16px"}}>
                        {getVal(summary.selectedJobTotal, summary.selectedJobOnsite, summary.selectedJobOffshore) ?? 0}
                      </td>
                      <td style={{textAlign:"center",fontWeight:700,color:"#3b5bfd",fontSize:"16px"}}>
                        {getVal(summary.selectedSubmissionTotal, summary.selectedSubmissionOnsite, summary.selectedSubmissionOffshore) ?? 0}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(320px, 1fr))",gap:"20px"}}>
                {/* Client Breakdown */}
                <div style={{background:"#f8fafc",padding:"16px",borderRadius:"8px",border:"1px solid #e2e8f0"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"12px",flexWrap:"wrap",gap:"8px"}}>
                    <h4 style={{margin:0,fontSize:"14px",color:"#1e293b",fontWeight:600,display:"flex",alignItems:"center",gap:"8px"}}>
                      <Building2 size={16} color="#3b5bfd"/> Client Summary
                    </h4>
                    {/* Client Submissions Status Filter */}
                    <div style={{display:"flex",gap:"4px",background:"white",padding:"2px",borderRadius:"6px",border:"1px solid #cbd5e1"}}>
                      <button
                        type="button"
                        onClick={() => setClientSubFilter("all")}
                        style={{
                          padding: "3px 8px", fontSize: "11px", borderRadius: "4px", border: "none", cursor: "pointer",
                          fontWeight: clientSubFilter === "all" ? 700 : 500,
                          background: clientSubFilter === "all" ? "#3b5bfd" : "transparent",
                          color: clientSubFilter === "all" ? "white" : "#64748b"
                        }}
                      >
                        All ({breakdowns.byClient.length})
                      </button>
                      <button
                        type="button"
                        onClick={() => setClientSubFilter("with_subs")}
                        style={{
                          padding: "3px 8px", fontSize: "11px", borderRadius: "4px", border: "none", cursor: "pointer",
                          fontWeight: clientSubFilter === "with_subs" ? 700 : 500,
                          background: clientSubFilter === "with_subs" ? "#3b5bfd" : "transparent",
                          color: clientSubFilter === "with_subs" ? "white" : "#64748b"
                        }}
                      >
                        With Subs ({clientsWithSubsCount})
                      </button>
                      <button
                        type="button"
                        onClick={() => setClientSubFilter("without_subs")}
                        style={{
                          padding: "3px 8px", fontSize: "11px", borderRadius: "4px", border: "none", cursor: "pointer",
                          fontWeight: clientSubFilter === "without_subs" ? 700 : 500,
                          background: clientSubFilter === "without_subs" ? "#ef4444" : "transparent",
                          color: clientSubFilter === "without_subs" ? "white" : "#64748b"
                        }}
                      >
                        ⚠️ Without Subs ({clientsWithoutSubsCount})
                      </button>
                    </div>
                  </div>

                  {filteredClientBreakdown.length === 0 ? (
                    <p style={{fontSize:"13px",color:"#94a3b8",margin:"20px 0",textAlign:"center"}}>
                      {clientSubFilter === "without_subs" ? "No clients with 0 submissions." : "No client data available for this range."}
                    </p>
                  ) : (
                    <div style={{maxHeight:"320px",overflowY:"auto",borderRadius:"4px",border:"1px solid #e2e8f0"}}>
                      <table className="jcr-table">
                        <thead style={{position:"sticky",top:0,background:"#f8fafc",zIndex:1}}>
                          <tr>
                            <th>Client</th>
                            <th style={{textAlign:"right"}}>Jobs</th>
                            <th style={{textAlign:"right"}}>Submissions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredClientBreakdown.map((item,idx)=>{
                            const hasZeroSubs = (item.submissionCount || 0) === 0;
                            return (
                              <tr key={idx} style={{ background: hasZeroSubs && clientSubFilter === "all" ? "#fffbeb" : "inherit" }}>
                                <td style={{ fontWeight: 500 }}>
                                  {item.clientName || "Unknown"}
                                  {hasZeroSubs && (
                                    <button
                                      type="button"
                                      onClick={() => setMissingModalClient(item.clientName)}
                                      style={{
                                        marginLeft: "8px", fontSize: "10px", background: "#fef2f2",
                                        color: "#dc2626", padding: "2px 8px", borderRadius: "4px",
                                        fontWeight: 700, border: "1px solid #fecaca", cursor: "pointer"
                                      }}
                                      title={`View unsubmitted jobs for ${item.clientName}`}
                                    >
                                      No Subs ↗
                                    </button>
                                  )}
                                </td>
                                <td style={{textAlign:"right",fontWeight:600}}>{item.jobCount}</td>
                                <td style={{textAlign:"right",fontWeight:600,color: hasZeroSubs ? "#94a3b8" : "#3b5bfd"}}>
                                  {hasZeroSubs ? (
                                    <span style={{ color: "#ef4444", fontWeight: 700 }}>0</span>
                                  ) : (
                                    item.submissionCount
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                {/* Work Mode Breakdown */}
                <div style={{background:"#f8fafc",padding:"16px",borderRadius:"8px",border:"1px solid #e2e8f0"}}>
                  <h4 style={{margin:"0 0 12px 0",fontSize:"14px",color:"#1e293b",fontWeight:600,display:"flex",alignItems:"center",gap:"8px"}}><Laptop size={16} color="#3b82f6"/> Work Mode Summary</h4>
                  {breakdowns.byWorkMode.length===0 ? (<p style={{fontSize:"13px",color:"#94a3b8",margin:0}}>No work mode data for this range.</p>) : (
                    <div style={{maxHeight:"320px",overflowY:"auto",borderRadius:"4px",border:"1px solid #e2e8f0"}}>
                      <table className="jcr-table">
                        <thead style={{position:"sticky",top:0,background:"#f8fafc",zIndex:1}}><tr><th>Work Mode</th><th style={{textAlign:"right"}}>Job Count</th></tr></thead>
                        <tbody>{breakdowns.byWorkMode.map((item,idx)=>(<tr key={idx}><td>{item.workMode||"Unspecified"}</td><td style={{textAlign:"right",fontWeight:600,color:"#3b82f6"}}>{item.jobCount}</td></tr>))}</tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Missing Submissions Modal */}
      {missingModalClient && (
        <MissingSubmissionsModal
          startDate={startDate}
          endDate={endDate}
          region={regionFilter}
          client={missingModalClient === 'all' ? null : missingModalClient}
          creationType={creationType}
          onClose={() => setMissingModalClient(null)}
        />
      )}
    </div>
  );
};

export default JobCountReport;
